<template>
    <div class="reader min-h-screen bg-reader-bg text-reader-text transition-colors duration-300"
         :class="themeClasses">
        <!-- 顶部工具栏 -->
        <header class="bg-white dark:bg-gray-800 shadow-md py-3 px-4 sticky top-0 z-10 transition-colors duration-300"
                :class="themeClasses">
            <div class="container mx-auto flex items-center justify-between">
                <!-- 书名 -->
                <div v-if="NovelData"
                     class="flex items-center space-x-2">
                    <button @click="goBack" class="flex items-center text-reader-text hover:text-primary">
                        <i class="fa fa-arrow-left mr-2"></i>
                    </button>
                    <h1 class="text-xl font-bold text-gray-800 dark:text-white truncate max-w-xs md:max-w-md">
                        <input
                            v-model="NovelData.title"
                            type="text"
                            placeholder="请输入书名"
                            class="bg-transparent border-b-2 border-transparent focus:border-primary text-xl font-bold text-gray-800 dark:text-white w-full outline-none transition-colors duration-200"
                        >
                    </h1>
                </div>
                <!-- 字数统计 -->
                <div
                    class="bg-white dark:bg-gray-800 py-2 px-4 text-center text-sm text-gray-600 dark:text-gray-400 transition-colors"
                    :class="themeClasses">
                    <p>当前字数: {{ wordCount }}</p>
                </div>
                <!-- 工具栏按钮 -->
                <div class="flex items-center space-x-3">
                    <!-- 字体大小调整 -->
                    <button
                        @click="decreaseFontSize"
                        class="text-reader-text hover:text-primary "
                        title="减小字体"
                    >
                        <i class="fa fa-font text-sm"></i>
                    </button>
                    <span class="text-xs">{{ fontSize }}px</span>
                    <button
                        @click="increaseFontSize"
                        class="text-reader-text hover:text-primary"
                        title="增大字体"
                    >
                        <i class="fa fa-font text-lg"></i>
                    </button>

                    <!-- 主题切换 -->
                    <button
                        @click="toggleTheme"
                        class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                        title="切换主题"
                    >
                        <i :class="themeIcon"></i>
                    </button>

                    <!-- 上传按钮 -->
                    <button
                        @click="saveNovel"
                        class="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center"
                    >
                        <i class="fa fa-cloud-upload mr-2"></i>
                        <span>保存草稿</span>
                    </button>
                    <!-- 上传按钮 -->
                    <button
                        @click="putNovel"
                        class="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center"
                    >
                        <i class="fa fa-cloud-upload mr-2"></i>
                        <span>上传章节</span>
                    </button>
                </div>
            </div>
        </header>

        <!-- 主要内容区 -->
        <main class="flex-grow container mx-auto p-4 md:p-8 flex justify-center"
        >
            <!-- 编辑器区域 -->
            <div
                class="mx-auto px-4 py-10 md:py-20 w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 md:p-10 transition-colors "
                :class="themeClasses"
                :style="{ 'font-size': `${fontSize}px`, 'line-height': lineHeight }">
                <!-- 章节标题 -->
                <div class="mb-6">
                    <input
                        v-model="title"
                        type="text"
                        placeholder="请输入章节标题"
                        class="w-full bg-transparent border-b-2 border-gray-200 dark:border-gray-700 focus:border-primary text-2xl font-bold text-gray-800 dark:text-white outline-none transition-colors "
                    >
                </div>
                <!-- 文本编辑区域 -->
                <div
                    ref="editorRef"
                    class="prose prose-lg prose-slate max-w-none text-reader-text"
                    contenteditable="true"
                    @keydown="handleKeydown"
                    @input="handleInput"
                >
                    <!-- 初始化段落（示例数据） -->
                    <div class="novel-content mx-auto prose max-w-none dark:prose-invert" v-if="htmlContent"
                         v-html="htmlContent"></div>
                </div>
            </div>
        </main>
        <!-- 提示框容器 -->
        <div
            v-if="isShow"
            class="toast"
            :class="{ 'show': isShow }"
        >
            保存成功！
        </div>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import {useRouter} from 'vue-router'
import {storeToRefs} from "pinia";
import {editorDraftStore} from "@/store/editorDraft";
import {draftAndPutDraftWebSocket} from "@/services/DraftAndPutDraftWebSocket";
import {debounce} from 'lodash-es';
import dayjs from 'dayjs';

const isShow = ref(false)

const chapterStore = editorDraftStore();
// 将 currentChapter 转换为 ref
let {currentChapter} = storeToRefs(chapterStore);
// 使用计算属性安全获取嵌套值
const currentId = computed(() => currentChapter.value.id);
const novel_id = computed(() => currentChapter.value.novel_id);
const title = computed({
    get() {
        return currentChapter.value.title;
    },
    set(newValue) {
        chapterStore.updateChapterTitle(newValue);
    }
});
const {
    connected, novels, draft_content,
    loading, errorItNovel, error,
    fetchNovelsChapter, updateDraftChapter, putDraftChapter
} = draftAndPutDraftWebSocket(currentId, novel_id);
const loadData = async () => {
    fetchNovelsChapter();
};
const NovelData = computed(() => {
    const defaultValue = {
        id: '0',
        novelId: '0',
        title: '加载中...',
        description: '加载中…',
        coverImage: '/cover_picture/loading.jpg',
        type: '……',
        updatedAt: '2005-01-25 00:00:00'
    };

    // 合并数据
    return {...defaultValue, ...(novels.value || {})};
});
const processedContent = computed(() => {
    const defaultValue = {
        id: '0',
        novelId: '0',
        content: '…',
    };
    // 合并数据
    return {...defaultValue, ...(draft_content.value || {})};
});

const editorRef = ref(null);
// 计算属性：字数统计
const wordCount = ref(null);
// 键盘事件处理
const handleKeydown = (e) => {
    const {key, target} = e;
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const targetElement = target instanceof HTMLElement ? target : null;
    const container = range.startContainer;
    // 获取文本内容长度
    const textLength = container.textContent.length;
    // 处理 Enter 键：创建新段落并缩进
    if (key === 'Enter') {
        e.preventDefault();
        // 获取当前光标所在的段落
        let currentParagraph = range.commonAncestorContainer;

        // 如果祖先不是段落元素，则向上查找直到找到段落
        while (currentParagraph && currentParagraph.tagName !== 'P') {
            currentParagraph = currentParagraph.parentElement;
        }

        if (!currentParagraph) {
            // 如果找不到段落元素（理论上不会发生），创建新段落
            const newParagraph = document.createElement('p');
            newParagraph.textContent = ' ';
            editorRef.value.appendChild(newParagraph);

            // 移动光标到新段落
            const newRange = document.createRange();
            newRange.selectNodeContents(newParagraph);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            return;
        }

        // 分割当前段落的文本内容
        const textNode = range.startContainer;


        // 如果光标在文本节点内
        if (textNode.nodeType === Node.TEXT_NODE) {
            // 获取光标在文本节点中的位置
            let offset = range.startOffset;
            // 分割文本节点
            const afterText = textNode.splitText(offset);

            if (offset === textLength && textLength !== 0) {
                // 在段落末尾
                const newParagraph = document.createElement('p');
                newParagraph.innerHTML = '<br>';
                newParagraph.className = 'text-left mb-4'
                newParagraph.style.textIndent = "2em"
                // 将新段落插入到当前段落之后
                currentParagraph.after(newParagraph);
                // 移除原段落中的剩余文本
                afterText.remove();
                // 移动光标到新段落
                const newRange = document.createRange();
                newRange.selectNodeContents(newParagraph);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
            } else if (offset === 0) {
                const newParagraph = document.createElement('p');
                newParagraph.innerHTML = '<br>';
                newParagraph.className = 'text-left mb-4';
                newParagraph.style.textIndent = '2em';
                // 将新段落插入到当前段落之前
                currentParagraph.before(newParagraph);
                // 移动光标到新段落
                positionCursorAtOffset(currentParagraph, 0);
            } else {
                // 在段落中间
                const newParagraph = document.createElement('p');
                newParagraph.innerHTML = '' + afterText.textContent;
                newParagraph.className = 'text-left mb-4'
                newParagraph.style.textIndent = "2em"
                // 将新段落插入到当前段落之后
                currentParagraph.after(newParagraph);
                // 移除原段落中的剩余文本
                afterText.remove();
                // 移动光标到新段落
                positionCursorAtOffset(newParagraph, 0);
            }
        } else {
            const newParagraph = document.createElement('p');
            newParagraph.innerHTML = '<br>';
            newParagraph.className = 'text-left mb-4'
            newParagraph.style.textIndent = "2em"
            // 将新段落插入到当前段落之后
            currentParagraph.after(newParagraph);
            // 移动光标到新段落
            const newRange = document.createRange();
            newRange.selectNodeContents(newParagraph);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }
    }
    // 处理 Backspace 键（合并段落）
    if (e.key === 'Backspace') {
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        // 检查光标是否在段落开头
        if (range.collapsed && range.startOffset === 0) {
            // 获取包含光标的段落
            const container = range.startContainer;
            const currentParagraph = container.nodeType === Node.TEXT_NODE
                ? container.parentElement.closest('p')
                : container.closest('p');
            // 检查段落是否为空
            if (currentParagraph.previousElementSibling === null) {
                e.preventDefault();
            } else {
                if (currentParagraph && currentParagraph.textContent.trim() === '') {
                    e.preventDefault();
                    const previousParagraph = currentParagraph.previousElementSibling;
                    if (previousParagraph && previousParagraph.tagName === 'P') {
                        // 合并内容
                        currentParagraph.remove();
                        // 移动光标
                        const newRange = document.createRange();
                        newRange.selectNodeContents(previousParagraph);
                        newRange.collapse(false);
                        selection.removeAllRanges();
                        selection.addRange(newRange);
                    }
                } else {
                    e.preventDefault();
                    const previousParagraph = currentParagraph.previousElementSibling;
                    if (previousParagraph && previousParagraph.textContent.trim() === '') {
                        previousParagraph.innerHTML = '' + currentParagraph.textContent;
                        currentParagraph.remove();
                        // 移动光标
                        const newRange = document.createRange();
                        newRange.selectNodeContents(previousParagraph);
                        newRange.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(newRange);
                    } else {
                        const length = previousParagraph.textContent.length;
                        previousParagraph.textContent = previousParagraph.textContent + currentParagraph.textContent;
                        currentParagraph.remove();
                        positionCursorAtOffset(previousParagraph, length);
                    }
                }
            }
        }
    }
};
// 辅助函数：在元素的指定偏移位置设置光标
const positionCursorAtOffset = (element, offset) => {
    const selection = window.getSelection();
    const range = document.createRange();
    // 获取元素内的所有文本节点
    const textNodes = getTextNodes(element);
    let currentOffset = 0;
    let targetNode = null;
    let targetOffset = 0;
    // 遍历文本节点，找到对应偏移位置的节点
    for (const node of textNodes) {
        const nodeLength = node.textContent.length;
        // 如果当前节点包含目标偏移位置
        if (currentOffset + nodeLength >= offset) {
            targetNode = node;
            targetOffset = offset - currentOffset;
            break;
        }
        currentOffset += nodeLength;
    }
    // 如果找到了目标节点，设置光标位置
    if (targetNode) {
        range.setStart(targetNode, targetOffset);
        range.setEnd(targetNode, targetOffset);
    } else {
        // 如果没找到（可能是偏移量超出了元素内容长度）
        // 将光标放在元素末尾
        range.selectNodeContents(element);
        range.collapse(false);
    }
    selection.removeAllRanges();
    selection.addRange(range);
};
// 辅助函数：获取元素内的所有文本节点
const getTextNodes = (element) => {
    const textNodes = [];
    const walk = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null,
        false
    );
    let node;
    while (node = walk.nextNode()) {
        textNodes.push(node);
    }
    return textNodes;
};
// 清理段落中的 span 标签，保留内容
const cleanSpans = (paragraph) => {
    // 获取所有 span 标签
    const spans = Array.from(paragraph.querySelectorAll('span'));

    // 按 DOM 顺序处理（从后向前，避免索引变化）
    for (let i = spans.length - 1; i >= 0; i--) {
        const span = spans[i];
        // 1. 提取 span 内的所有子节点
        const children = Array.from(span.childNodes);
        // 2. 将子节点移动到父级段落
        if (children.length > 0) {
            span.before(...children);
        }
        // 3. 删除空的 span
        span.remove();
    }
    // 合并相邻的文本节点
    paragraph.normalize();
};
// 处理整个编辑器内容
const cleanEditorContent = (editorElement) => {
    const paragraphs = editorElement.querySelectorAll('p');
    paragraphs.forEach(cleanSpans);
};

// 字数统计函数
const updateWordCount = () => {
    // 获取所有文本内容（去除HTML标签）
    const textContent = editorRef.value.innerText || editorRef.value.textContent;

    // 中文按字符统计，英文按单词统计
    const chineseChars = textContent.match(/[\u4e00-\u9fa5]/g) || [];
    const englishWords = textContent.match(/\b[a-zA-Z]+\b/g) || [];

    // 计算总字数（中文字符数 + 英文单词数）
    wordCount.value = chineseChars.length + englishWords.length;

    // 或者简单的按所有非空格字符统计
    // wordCount.value = textContent.replace(/\s+/g, '').length;
};
// 使用防抖
const debouncedUpdateWordCount = debounce(updateWordCount, 300);
// 输入时自动清理无效标签
const handleInput = () => {
    // 清理编辑器中的 span 标签
    cleanEditorContent(editorRef.value);
    const paragraphs = Array.from(editorRef.value.querySelectorAll('p'));
    htmlContent.value = paragraphs
        .map(p => `<p class="text-left mb-4" style="text-indent: 2em">${p.innerHTML}</p>`)
        .join('');
    // 计算字数
    debouncedUpdateWordCount();
};

const escapeHTML = (str) => {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};
const htmlContent = computed(() => {
    console.log(processedContent.value.content)
    return processedContent.value.content
        .split('\n')
        .filter(line => line.trim() !== '') // 过滤空行
        .map(line => {
            const cleanedLine = line.replace(/[\n\r]/g, '');
            return `<p class="text-left mb-4" style="text-indent: 2em">${escapeHTML(cleanedLine)}</p>`;
        })
        .join('');
});
// 路由跳转函数
const router = useRouter()
// 状态管理
const bookTitle = ref('未命名小说');
const chapterTitle = ref('第一章');
const fontSize = ref(16); // 基准字体大小（px）
const lineHeight = ref('1.8')
const isDarkMode = ref(false);

// 导航函数
const goBack = () => {
    router.go(-1)
}
// 定义支持的主题
const themes = {
    light: {
        bgColor: 'bg-white',
        textColor: 'text-gray-800',
        icon: 'fa-sun-o', // 太阳图标表示可切换到亮色模式
    },
    dark: {
        bgColor: 'bg-moon_bg',
        textColor: 'text-moon_font',
        icon: 'fa-moon-o', // 月亮图标表示可切换到暗色模式
    },
    sepia: {
        bgColor: 'bg-eye_bg',
        textColor: 'text-eye_font',
        icon: 'fa-eye', // 眼睛图标表示护眼模式
    },
    love: {
        bgColor: 'bg-love_bg',
        textColor: 'text-love_font',
        icon: 'fa-heart-o', // 眼睛图标表示护眼模式
    },
    child: {
        bgColor: 'bg-child_bg',
        textColor: 'text-child_font',
        icon: 'fa-heart', // 眼睛图标表示护眼模式
    }
};
// 当前主题状态
const currentTheme = ref('light');

// 计算属性：根据当前主题生成样式类
const themeClasses = computed(() => {
    const theme = themes[currentTheme.value];
    return `${theme.bgColor} ${theme.textColor} transition-colors duration-300`;
});

// 计算属性：根据当前主题显示图标
const themeIcon = computed(() => {
    return `fa ${themes[currentTheme.value].icon}`;
});
// 主题切换
const toggleTheme = () => {
    const themeOrder = ['light', 'dark', 'sepia', 'love', 'child'];
    const currentIndex = themeOrder.indexOf(currentTheme.value);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    currentTheme.value = themeOrder[nextIndex];
}

// 字体大小调整
const increaseFontSize = () => {
    if (fontSize.value < 24) {
        fontSize.value += 2;
    }
};

const decreaseFontSize = () => {
    if (fontSize.value > 12) {
        fontSize.value -= 2;
    }
};

// 保存小说

const newChapterIntroduce = () => {
    return {
        id: currentId.value,
        novelId: novel_id.value,
        title: title.value,
        wordCount: wordCount.value,
        createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss')
    };
}
const newContent = () => {
    // 清理编辑器中的 span 标签
    cleanEditorContent(editorRef.value);
    const paragraphs = Array.from(editorRef.value.querySelectorAll('p'));
    const textContent = paragraphs
        .map(p => p.textContent)      // 提取每个p标签的文本
        .join('\n');                 // 用换行符连接
    return {
        id: currentId.value,
        novelId: novel_id.value,
        content: textContent,
    };
}
const saveNovel = () => {
    const updateIntroduce = newChapterIntroduce();
    const updateContent = newContent();
    updateDraftChapter(updateIntroduce, updateContent)
    isShow.value = true;

    // 2秒后隐藏
    setTimeout(() => {
        isShow.value = false;
    }, 2000);
};
const putNovel = async () => {
    const updateIntroduce = newChapterIntroduce();
    const updateContent = newContent();
    await putDraftChapter(updateIntroduce, updateContent)
    goBack()
};
// 初始化主题
onMounted(() => {
    nextTick(() => {
        const editor = editorRef.value;
        if (editor) {
            const range = document.createRange();
            range.selectNode(editor.lastChild || editor);
            range.collapse(false);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }
    });
    // 检查系统主题偏好
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    isDarkMode.value = prefersDark;
    document.documentElement.classList.toggle('dark', prefersDark);
    loadData()
    // 初始化时统计字数
    if (editorRef.value) {
        debouncedUpdateWordCount();
    }
});

// 监听字体大小变化，更新样式
watch(fontSize, (newSize) => {
    document.documentElement.style.setProperty('--editor-font-size', `${newSize}px`);
});
</script>

<style scoped>
.toast {
    position: fixed;
    top: 0;
    left: 50%;
    z-index: 1000;
    background-color: #333;
    color: white;
    padding: 12px 24px;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
    transition: all 0.3s ease;
    max-width: 80%;
    text-align: center;
}

.toast.show {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
}

.novel-content p {
    text-align: left !important; /* 设置文本左对齐 */
    text-indent: 2em; /* 保留首行缩进 */
    margin-bottom: 16px; /* 段落间距 */
}

.novel-editor {
    font-family: 'Noto Sans SC', sans-serif;
    font-size: 16px;
    line-height: 1.8;
}

[contenteditable] {
    outline: none;
    white-space: pre-wrap;
}

[contenteditable] p {
    margin: 0;
    padding: 8px 0;
    text-indent: 2em; /* 配合全角空格实现双倍缩进 */
}

[contenteditable] p:first-child {
    padding-top: 0;
}

[contenteditable] p:last-child {
    padding-bottom: 0;
}


/* 自定义变量 */
:root {
    --editor-font-size: 16px;
}

/* 编辑器样式 */
textarea {
    font-size: var(--editor-font-size);
    line-height: 3;
}


</style>